"use client";

import { useEffect, useRef, useState } from "react";

interface AnimatedPriceProps {
  value: number;
  format?: (v: number) => string;
  duration?: number; // ms, default 500
  className?: string;
}

function toSafeNumber(value: number): number {
  return Number.isFinite(value) ? value : 0;
}

/**
 * Smoothly animates between price values like an odometer.
 * Uses requestAnimationFrame for smooth interpolation.
 */
export default function AnimatedPrice({
  value,
  format,
  duration = 500,
  className,
}: AnimatedPriceProps) {
  const safeValue = toSafeNumber(value);
  const [displayValue, setDisplayValue] = useState(safeValue);
  const animRef = useRef<number | null>(null);
  const startRef = useRef({ value: safeValue, time: 0 });
  const targetRef = useRef(safeValue);

  useEffect(() => {
    if (safeValue === targetRef.current) return;

    // Cancel previous animation
    if (animRef.current) cancelAnimationFrame(animRef.current);

    startRef.current = { value: displayValue, time: performance.now() };
    targetRef.current = safeValue;

    const animate = (now: number) => {
      const elapsed = now - startRef.current.time;
      const progress = Math.min(elapsed / duration, 1);

      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);

      const current = startRef.current.value + (targetRef.current - startRef.current.value) * eased;
      setDisplayValue(current);

      if (progress < 1) {
        animRef.current = requestAnimationFrame(animate);
      } else {
        animRef.current = null;
      }
    };

    animRef.current = requestAnimationFrame(animate);

    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, [safeValue, duration]);

  // Initialize on first non-zero value
  useEffect(() => {
    if (displayValue === 0 && safeValue > 0) {
      setDisplayValue(safeValue);
      targetRef.current = safeValue;
      startRef.current = { value: safeValue, time: 0 };
    }
  }, [safeValue, displayValue]);

  const safeDisplayValue = toSafeNumber(displayValue);
  const formatted = format ? format(safeDisplayValue) : safeDisplayValue.toFixed(2);

  return <span className={className}>{formatted}</span>;
}
